import type { ReportType } from '../types/workflow';

// PRIIPs has a dedicated revision lifecycle and must fail closed in period-based navigation.
type PeriodBasedReportType = Exclude<ReportType, 'PRIIPs'>;

export const WORKFLOW_STAGE_KEYS = {
  DASHBOARD: 'dashboard',
  AIFS: 'aifs',
  JURISDICTIONS: 'jurisdictions',
  SOURCE_FILES: 'source_files',
  SETUP: 'setup',
  EXTRACTION: 'extraction',
  TRANSFORMATION: 'transformation',
  LOOKUP_TABLES: 'lookup_tables',
  OUTPUT_DATA: 'output_data',
  REPORT: 'report',
  APPROVAL: 'approval',
  SUBMISSION: 'submission'
} as const;

export type WorkflowStageKey = (typeof WORKFLOW_STAGE_KEYS)[keyof typeof WORKFLOW_STAGE_KEYS];

export type WorkflowStatusStrategy = 'annex' | 'form-pf' | 'custom' | 'conversion';
export type ClassicReportRouteKind = 'jurisdictional' | 'form-pf';
export type PeriodOverviewStepKey =
  | 'aif'
  | 'jurisdictions'
  | 'files'
  | 'mapping'
  | 'extraction'
  | 'lookup'
  | 'transformation'
  | 'report'
  | 'approval'
  | 'submission';

export interface WorkflowProductProfile {
  reportType: PeriodBasedReportType;
  nouns: {
    fund: string;
    jurisdiction: string;
  };
  statusStrategy: WorkflowStatusStrategy;
  classicReportRouteKind: ClassicReportRouteKind;
  requiresJurisdictionForReport: boolean;
  alwaysShowDashboard: boolean;
  entryStage: WorkflowStageKey;
  stepperStages: readonly WorkflowStageKey[];
  periodSwitchStages: readonly WorkflowStageKey[];
  periodOverviewSteps: readonly PeriodOverviewStepKey[];
  filingWindowDays: number;
}

const STANDARD_STEPPER_STAGES = [
  WORKFLOW_STAGE_KEYS.DASHBOARD,
  WORKFLOW_STAGE_KEYS.AIFS,
  WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  WORKFLOW_STAGE_KEYS.SETUP,
  WORKFLOW_STAGE_KEYS.EXTRACTION,
  WORKFLOW_STAGE_KEYS.LOOKUP_TABLES,
  WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
  WORKFLOW_STAGE_KEYS.REPORT,
  WORKFLOW_STAGE_KEYS.APPROVAL,
  WORKFLOW_STAGE_KEYS.SUBMISSION
] as const;

export const STANDARD_PERIOD_SWITCH_STAGES = [
  WORKFLOW_STAGE_KEYS.DASHBOARD,
  WORKFLOW_STAGE_KEYS.AIFS,
  WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  WORKFLOW_STAGE_KEYS.EXTRACTION,
  WORKFLOW_STAGE_KEYS.LOOKUP_TABLES,
  WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
  WORKFLOW_STAGE_KEYS.REPORT,
  WORKFLOW_STAGE_KEYS.APPROVAL,
  WORKFLOW_STAGE_KEYS.SUBMISSION
] as const;

const STANDARD_PERIOD_OVERVIEW_STEPS = [
  'aif',
  'jurisdictions',
  'files',
  'mapping',
  'extraction',
  'lookup',
  'transformation',
  'report',
  'approval',
  'submission'
] as const;

const CONVERSION_STAGES = [
  WORKFLOW_STAGE_KEYS.DASHBOARD,
  WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  WORKFLOW_STAGE_KEYS.AIFS,
  WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  WORKFLOW_STAGE_KEYS.REPORT,
  WORKFLOW_STAGE_KEYS.APPROVAL,
  WORKFLOW_STAGE_KEYS.SUBMISSION
] as const;

const CONVERSION_OVERVIEW_STEPS = [
  'files',
  'aif',
  'jurisdictions',
  'report',
  'approval',
  'submission'
] as const;

const CUSTOM_OVERVIEW_STEPS = [
  'files',
  'mapping',
  'extraction',
  'lookup',
  'transformation',
  'report'
] as const;

export const WORKFLOW_PROFILES = {
  'Annex IV': {
    reportType: 'Annex IV',
    nouns: { fund: 'AIF', jurisdiction: 'Jurisdiction' },
    statusStrategy: 'annex',
    classicReportRouteKind: 'jurisdictional',
    requiresJurisdictionForReport: true,
    alwaysShowDashboard: false,
    entryStage: WORKFLOW_STAGE_KEYS.AIFS,
    stepperStages: STANDARD_STEPPER_STAGES,
    periodSwitchStages: STANDARD_PERIOD_SWITCH_STAGES,
    periodOverviewSteps: STANDARD_PERIOD_OVERVIEW_STEPS,
    filingWindowDays: 31
  },
  'Form PF': {
    reportType: 'Form PF',
    nouns: { fund: 'Fund', jurisdiction: 'Fund Info' },
    statusStrategy: 'form-pf',
    classicReportRouteKind: 'form-pf',
    requiresJurisdictionForReport: false,
    alwaysShowDashboard: false,
    entryStage: WORKFLOW_STAGE_KEYS.AIFS,
    stepperStages: STANDARD_STEPPER_STAGES,
    periodSwitchStages: STANDARD_PERIOD_SWITCH_STAGES,
    periodOverviewSteps: STANDARD_PERIOD_OVERVIEW_STEPS,
    filingWindowDays: 60
  },
  Custom: {
    reportType: 'Custom',
    nouns: { fund: 'Asset', jurisdiction: 'Jurisdiction' },
    statusStrategy: 'custom',
    classicReportRouteKind: 'jurisdictional',
    requiresJurisdictionForReport: true,
    alwaysShowDashboard: false,
    entryStage: WORKFLOW_STAGE_KEYS.AIFS,
    stepperStages: STANDARD_STEPPER_STAGES,
    periodSwitchStages: STANDARD_PERIOD_SWITCH_STAGES.filter(
      stage => stage !== WORKFLOW_STAGE_KEYS.JURISDICTIONS
    ),
    periodOverviewSteps: CUSTOM_OVERVIEW_STEPS,
    filingWindowDays: 31
  },
  Conversion: {
    reportType: 'Conversion',
    nouns: { fund: 'AIF', jurisdiction: 'Jurisdiction' },
    statusStrategy: 'conversion',
    classicReportRouteKind: 'jurisdictional',
    requiresJurisdictionForReport: true,
    alwaysShowDashboard: true,
    entryStage: WORKFLOW_STAGE_KEYS.AIFS,
    stepperStages: CONVERSION_STAGES,
    periodSwitchStages: CONVERSION_STAGES,
    periodOverviewSteps: CONVERSION_OVERVIEW_STEPS,
    filingWindowDays: 31
  }
} satisfies Record<PeriodBasedReportType, WorkflowProductProfile>;

export const getWorkflowProfile = (
  reportType?: string | null
): WorkflowProductProfile | undefined => {
  if (!reportType || !Object.prototype.hasOwnProperty.call(WORKFLOW_PROFILES, reportType)) {
    return undefined;
  }

  return WORKFLOW_PROFILES[reportType as PeriodBasedReportType];
};
